/**
 * Resource Panel Component
 * 
 * Displays resource totals, weights, and add/remove controls.
 * Ported from the Resources tab in main.mxml
 */

import { settings } from '../utils/Settings.js';
import { 
    getBuildingCost, 
    getTotalCostToLevel,
    formatBuildTime 
} from '../core/BuildingData.js';
import { 
    CUTTER, CUTTER_OLD, QUARRY, QUARRY_OLD, MINE, MINE_OLD,
    FARM, FARM_OLD, MANOR, CASA, CELLARIUM, CACHE,
    WHARF, BAZAAR, TOWN_HALL, WALL, SAWMILL, STONEMASON,
    FOUNDRY, MILL, BARRACKS, CITY_GUARD_HOUSE, TRAINING_GROUND,
    STABLES, MOONGLOW, SHIPYARD, WORKSHOP, CASTLE
} from '../core/Constants.js';
import { isBuilding } from '../core/Constants.js';

export class ResourcePanel {
    constructor(containerElement, gameEngine) {
        this.container = containerElement;
        this.gameEngine = gameEngine;
        
        // Callbacks
        this.onWeightChange = null;
        this.onAddBuilding = null;
        this.onRemoveBuilding = null;
        this.onAddBuildings = null;
        this.onRemoveBuildings = null;
        this.onAddCabins = null;
        this.onRemoveCabins = null;
        this.onLockAllBuildings = null;
        this.onLockAllResources = null;
        this.onUnlockAll = null;
        this.onUndo = null;
        this.onRedo = null;
        this.onResetMap = null;
        
        // Store layout resources data
        this.layoutResources = null;
        
        this.render();
    }
    
    /**
     * Render the resource panel
     */
    render() {
        if (!this.gameEngine) return;
        
        const { gameEngine } = this;
        
        this.container.innerHTML = `
            <div class="resource-panel">
                <!-- Resource Cards Grid -->
                <div class="resource-grid">
                    <!-- Wood Card -->
                    <div class="res-card">
                        <div class="res-head">
                            <div class="res-head-left">
                                <img src="./assets/images/ui/icon_resource_wood.png" alt="Wood" class="resource-icon" onerror="this.style.display='none'">
                                <span>Wood</span>
                            </div>
                            <div class="res-actions">
                                <button class="btn-small" data-action="add" data-resource="wood">+</button>
                                <button class="btn-small" data-action="remove" data-resource="wood">-</button>
                            </div>
                        </div>
                        <div class="res-row">
                            <span>Production:</span>
                            <span class="resource-value">${this.formatNumber(gameEngine.wood_tot)}</span>
                        </div>
                        <div class="res-row">
                            <span>Storage:</span>
                            <span>${this.formatNumber(gameEngine.wood_storage)}</span>
                            <span class="storage-hidden">+${this.formatNumber(gameEngine.wood_hidden)}</span>
                        </div>
                        <div class="weight-section">
                            <div class="weight-label">
                                Weight
                                <input type="number" class="weight-input" id="weight-input-wood" min="0" max="3" step="0.1" value="${gameEngine.wood_weight.toFixed(2)}">
                                <span class="weight-value">${gameEngine.wood_weight.toFixed(2)}</span>
                            </div>
                            <div class="weight-control">
                                <label>
                                    <input type="range" id="weight-wood" min="0" max="3" step="0.1" value="${gameEngine.wood_weight}">
                                </label>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Stone Card -->
                    <div class="res-card">
                        <div class="res-head">
                            <div class="res-head-left">
                                <img src="./assets/images/ui/icon_resource_stone.png" alt="Stone" class="resource-icon" onerror="this.style.display='none'">
                                <span>Stone</span>
                            </div>
                            <div class="res-actions">
                                <button class="btn-small" data-action="add" data-resource="stone">+</button>
                                <button class="btn-small" data-action="remove" data-resource="stone">-</button>
                            </div>
                        </div>
                        <div class="res-row">
                            <span>Production:</span>
                            <span class="resource-value">${this.formatNumber(gameEngine.stone_tot)}</span>
                        </div>
                        <div class="res-row">
                            <span>Storage:</span>
                            <span>${this.formatNumber(gameEngine.stone_storage)}</span>
                            <span class="storage-hidden">+${this.formatNumber(gameEngine.stone_hidden)}</span>
                        </div>
                        <div class="weight-section">
                            <div class="weight-label">
                                Weight
                                <input type="number" class="weight-input" id="weight-input-stone" min="0" max="3" step="0.1" value="${gameEngine.stone_weight.toFixed(2)}">
                                <span class="weight-value">${gameEngine.stone_weight.toFixed(2)}</span>
                            </div>
                            <div class="weight-control">
                                <label>
                                    <input type="range" id="weight-stone" min="0" max="3" step="0.1" value="${gameEngine.stone_weight}">
                                </label>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Iron Card -->
                    <div class="res-card">
                        <div class="res-head">
                            <div class="res-head-left">
                                <img src="./assets/images/ui/icon_resource_iron.png" alt="Iron" class="resource-icon" onerror="this.style.display='none'">
                                <span>Iron</span>
                            </div>
                            <div class="res-actions">
                                <button class="btn-small" data-action="add" data-resource="iron">+</button>
                                <button class="btn-small" data-action="remove" data-resource="iron">-</button>
                            </div>
                        </div>
                        <div class="res-row">
                            <span>Production:</span>
                            <span class="resource-value">${this.formatNumber(gameEngine.iron_tot)}</span>
                        </div>
                        <div class="res-row">
                            <span>Storage:</span>
                            <span>${this.formatNumber(gameEngine.iron_storage)}</span>
                            <span class="storage-hidden">+${this.formatNumber(gameEngine.iron_hidden)}</span>
                        </div>
                        <div class="weight-section">
                            <div class="weight-label">
                                Weight
                                <input type="number" class="weight-input" id="weight-input-iron" min="0" max="3" step="0.1" value="${gameEngine.iron_weight.toFixed(2)}">
                                <span class="weight-value">${gameEngine.iron_weight.toFixed(2)}</span>
                            </div>
                            <div class="weight-control">
                                <label>
                                    <input type="range" id="weight-iron" min="0" max="3" step="0.1" value="${gameEngine.iron_weight}">
                                </label>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Food Card -->
                    <div class="res-card">
                        <div class="res-head">
                            <div class="res-head-left">
                                <img src="./assets/images/ui/icon_resource_grain.png" alt="Food" class="resource-icon" onerror="this.style.display='none'">
                                <span>Food</span>
                            </div>
                            <div class="res-actions">
                                <button class="btn-small" data-action="add" data-resource="food">+</button>
                                <button class="btn-small" data-action="remove" data-resource="food">-</button>
                            </div>
                        </div>
                        <div class="res-row">
                            <span>Production:</span>
                            <span class="resource-value">${this.formatNumber(gameEngine.food_tot)}</span>
                        </div>
                        <div class="res-row">
                            <span>Storage:</span>
                            <span>${this.formatNumber(gameEngine.food_storage)}</span>
                            <span class="storage-hidden">+${this.formatNumber(gameEngine.food_hidden)}</span>
                        </div>
                        <div class="weight-section">
                            <div class="weight-label">
                                Weight
                                <input type="number" class="weight-input" id="weight-input-food" min="0" max="3" step="0.1" value="${gameEngine.food_weight.toFixed(2)}">
                                <span class="weight-value">${gameEngine.food_weight.toFixed(2)}</span>
                            </div>
                            <div class="weight-control">
                                <label>
                                    <input type="range" id="weight-food" min="0" max="3" step="0.1" value="${gameEngine.food_weight}">
                                </label>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Gold Card -->
                    <div class="res-card">
                        <div class="res-head">
                            <div class="res-head-left">
                                <img src="./assets/images/ui/icon_resource_gold.png" alt="Gold" class="resource-icon" onerror="this.style.display='none'">
                                <span>Gold</span>
                            </div>
                            <div class="res-actions">
                                <button class="btn-small" data-action="add" data-resource="gold">+</button>
                                <button class="btn-small" data-action="remove" data-resource="gold">-</button>
                            </div>
                        </div>
                        <div class="res-row">
                            <span>Production:</span>
                            <span class="resource-value">${this.formatNumber(gameEngine.gold_tot)}</span>
                        </div>
                        <div class="res-row">
                            <span>Storage:</span>
                            <span>${this.formatNumber(gameEngine.gold_storage || 0)}</span>
                        </div>
                        <div class="weight-section">
                            <div class="weight-label">
                                Weight
                                <input type="number" class="weight-input" id="weight-input-gold" min="0" max="3" step="0.1" value="${gameEngine.gold_weight.toFixed(2)}">
                                <span class="weight-value">${gameEngine.gold_weight.toFixed(2)}</span>
                            </div>
                            <div class="weight-control">
                                <label>
                                    <input type="range" id="weight-gold" min="0" max="3" step="0.1" value="${gameEngine.gold_weight}">
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Optimization Section -->
                <div class="optimization-section" data-section-id="bulk-optimization">
                    <h3>Bulk Optimization</h3>
                    <div class="optimization-cards">
                        <!-- Buildings and Cabins Row -->
                        <div class="optimization-cards-row">
                            <!-- Buildings Optimization Card -->
                            <div class="optimization-card">
                                <div class="optimization-card-header">
                                    <h4>Buildings</h4>
                                    <div class="optimization-row">
                                        <button id="btn-add-buildings" class="btn-optimize">Add</button>
                                        <input type="number" id="n-buildings-add" class="optimization-input" min="1" value="10" />
                                        <button id="btn-remove-buildings" class="btn-optimize">Remove</button>
                                        <input type="number" id="n-buildings-remove" class="optimization-input" min="1" value="5" />
                                    </div>
                                </div>
                                <div class="optimization-card-body">
                                    <div class="optimization-note">
                                        Uses weighted optimization based on weights above
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Cabins Optimization Card -->
                            <div class="optimization-card">
                                <div class="optimization-card-header">
                                    <h4>Cabins</h4>
                                    <div style="display: flex; align-items: center; gap: 12px; flex-wrap: wrap;">
                                        <label style="display: flex; align-items: center; gap: 6px; cursor: pointer; font-size: 11px; color: var(--text-color);">
                                            <input type="checkbox" id="cabin-optimize-mode" style="cursor: pointer;" />
                                            <span>Optimize</span>
                                        </label>
                                        <div class="optimization-row">
                                            <button id="btn-add-cabins" class="btn-optimize">Add</button>
                                            <input type="number" id="n-cabins-add" class="optimization-input" min="1" value="10" />
                                            <button id="btn-remove-cabins" class="btn-optimize">Remove</button>
                                            <input type="number" id="n-cabins-remove" class="optimization-input" min="1" value="5" />
                                        </div>
                                    </div>
                                </div>
                                <div class="optimization-card-body">
                                    <div class="optimization-note" id="cabin-note">
                                        cabins place on OPEN spots, won't replace others
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Actions Card -->
                        <div class="optimization-card optimization-card-full-width">
                            <div class="optimization-card-header">
                                <h4>Actions</h4>
                            </div>
                            <div class="optimization-card-body">
                                <div class="optimization-action-row">
                                    <button id="btn-lock-buildings" class="btn-optimize-action">Lock All Buildings</button>
                                    <button id="btn-lock-resources" class="btn-optimize-action">Lock All Resources</button>
                                    <button id="btn-unlock-all" class="btn-optimize-action">Unlock All</button>
                                </div>
                                <div class="optimization-action-row">
                                    <button id="btn-undo" class="btn-optimize-action">Undo</button>
                                    <button id="btn-redo" class="btn-optimize-action">Redo</button>
                                    <button id="btn-reset-map" class="btn-optimize-action">Reset Map</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Layout Resources Section -->
                <div class="optimization-section" id="layout-resources-section" data-section-id="layout-resources" style="display: block;">
                    <h3>Layout Resources</h3>
                    <p style="font-size: 0.875rem; color: var(--text-secondary, #ccc); margin-bottom: 1rem;">
                        Set the resource levels for this layout. These will be saved with the layout.
                    </p>
                    <div class="layout-resources-container">
                        <div class="layout-resources-group">
                            <h4>Request Resources</h4>
                            <div class="layout-resources-grid">
                                <div class="layout-resource-item">
                                    <img src="./assets/images/ui/icon_resource_wood.png" alt="Wood" class="resource-icon-small" onerror="this.style.display='none'">
                                    <input type="number" class="layout-resource-input" id="request-wood" value="0" min="0" step="1">
                                </div>
                                <div class="layout-resource-item">
                                    <img src="./assets/images/ui/icon_resource_stone.png" alt="Stone" class="resource-icon-small" onerror="this.style.display='none'">
                                    <input type="number" class="layout-resource-input" id="request-stone" value="0" min="0" step="1">
                                </div>
                                <div class="layout-resource-item">
                                    <img src="./assets/images/ui/icon_resource_iron.png" alt="Iron" class="resource-icon-small" onerror="this.style.display='none'">
                                    <input type="number" class="layout-resource-input" id="request-iron" value="0" min="0" step="1">
                                </div>
                                <div class="layout-resource-item">
                                    <img src="./assets/images/ui/icon_resource_grain.png" alt="Food" class="resource-icon-small" onerror="this.style.display='none'">
                                    <input type="number" class="layout-resource-input" id="request-food" value="0" min="0" step="1">
                                </div>
                            </div>
                        </div>
                        <div class="layout-resources-group">
                            <h4>Excess Resources</h4>
                            <div class="layout-resources-grid">
                                <div class="layout-resource-item">
                                    <img src="./assets/images/ui/icon_resource_wood.png" alt="Wood" class="resource-icon-small" onerror="this.style.display='none'">
                                    <input type="number" class="layout-resource-input" id="excess-wood" value="0" min="0" step="1">
                                </div>
                                <div class="layout-resource-item">
                                    <img src="./assets/images/ui/icon_resource_stone.png" alt="Stone" class="resource-icon-small" onerror="this.style.display='none'">
                                    <input type="number" class="layout-resource-input" id="excess-stone" value="0" min="0" step="1">
                                </div>
                                <div class="layout-resource-item">
                                    <img src="./assets/images/ui/icon_resource_iron.png" alt="Iron" class="resource-icon-small" onerror="this.style.display='none'">
                                    <input type="number" class="layout-resource-input" id="excess-iron" value="0" min="0" step="1">
                                </div>
                                <div class="layout-resource-item">
                                    <img src="./assets/images/ui/icon_resource_grain.png" alt="Food" class="resource-icon-small" onerror="this.style.display='none'">
                                    <input type="number" class="layout-resource-input" id="excess-food" value="0" min="0" step="1">
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Total Building Costs Section -->
                <div class="optimization-section" data-section-id="total-building-costs">
                    <h3>Total Building Costs</h3>
                    ${this.renderTotalCosts()}
                </div>
            </div>
        `;
        
        // Setup event listeners
        this.setupEventListeners();
    }
    
    /**
     * Calculate total costs for all buildings in the city
     */
    calculateTotalCosts() {
        if (!this.gameEngine) {
            return {
                currentLevel: { wood: 0, stone: 0, buildTime: 0 },
                currentToNext: { wood: 0, stone: 0, buildTime: 0 },
                totalToCurrent: { wood: 0, stone: 0, buildTime: 0 },
                totalToLevel10: { wood: 0, stone: 0, buildTime: 0 }
            };
        }
        
        const buildingLevel = this.gameEngine.buildingLevel || 10;
        const basilicaLevel = this.gameEngine.basilicaLevel || 10;
        const wallLevel = this.gameEngine.wallLevel !== undefined && this.gameEngine.wallLevel !== null ? this.gameEngine.wallLevel : 10;
        const nextBuildingLevel = buildingLevel < 10 ? buildingLevel + 1 : null;
        const nextBasilicaLevel = basilicaLevel < 10 ? basilicaLevel + 1 : null;
        const nextWallLevel = wallLevel < 10 ? wallLevel + 1 : null;
        
        const totals = {
            currentLevel: { wood: 0, stone: 0, buildTime: 0 },
            currentToNext: { wood: 0, stone: 0, buildTime: 0 },
            totalToCurrent: { wood: 0, stone: 0, buildTime: 0 },
            totalToLevel10: { wood: 0, stone: 0, buildTime: 0 }
        };
        
        // Track if we've already counted the wall and basilica (they're single structures, not per-tile)
        let wallCounted = false;
        let basilicaCounted = false;
        
        // Iterate through all buildings on the map
        for (let i = 0; i < 21; i++) {
            for (let j = 0; j < 21; j++) {
                const tile = this.gameEngine.map[i][j];
                
                // Include buildings, walls, and basilica (town hall)
                const isABuilding = isBuilding(tile);
                const isWall = (tile === WALL);
                const isBasilica = (tile === TOWN_HALL);
                
                if (!isABuilding && !isWall && !isBasilica) continue;
                
                // Skip if we've already counted this wall or basilica (they're single structures)
                if (isWall && wallCounted) continue;
                if (isBasilica && basilicaCounted) continue;
                
                const buildingTypeKey = this.getBuildingTypeKey(tile);
                if (!buildingTypeKey) continue;
                
                // Mark as counted
                if (isWall) wallCounted = true;
                if (isBasilica) basilicaCounted = true;
                
                // Use basilicaLevel for TOWN_HALL, wallLevel for WALL, buildingLevel for all other buildings
                const level = isBasilica ? basilicaLevel : (isWall ? wallLevel : buildingLevel);
                const nextLevel = isBasilica ? nextBasilicaLevel : (isWall ? nextWallLevel : nextBuildingLevel);
                
                // Current level cost
                const currentCost = getBuildingCost(buildingTypeKey, level);
                totals.currentLevel.wood += currentCost.wood;
                totals.currentLevel.stone += currentCost.stone;
                totals.currentLevel.buildTime += currentCost.buildTime || 0;
                
                // Current to next level cost (if not max level) - this is the upgrade cost
                if (nextLevel) {
                    const nextCost = getBuildingCost(buildingTypeKey, nextLevel);
                    // Upgrade cost = next level cost - current level cost
                    totals.currentToNext.wood += (nextCost.wood - currentCost.wood);
                    totals.currentToNext.stone += (nextCost.stone - currentCost.stone);
                    totals.currentToNext.buildTime += ((nextCost.buildTime || 0) - (currentCost.buildTime || 0));
                }
                
                // Total to current level
                const totalToCurrent = getTotalCostToLevel(buildingTypeKey, level);
                totals.totalToCurrent.wood += totalToCurrent.wood;
                totals.totalToCurrent.stone += totalToCurrent.stone;
                totals.totalToCurrent.buildTime += totalToCurrent.buildTime || 0;
                
                // Total to level 10
                const totalToLevel10 = getTotalCostToLevel(buildingTypeKey, 10);
                totals.totalToLevel10.wood += totalToLevel10.wood;
                totals.totalToLevel10.stone += totalToLevel10.stone;
                totals.totalToLevel10.buildTime += totalToLevel10.buildTime || 0;
            }
        }
        
        return totals;
    }
    
    /**
     * Get building type key for cost lookup (same as BuildingStats)
     */
    getBuildingTypeKey(tile) {
        const keyMap = {
            [CUTTER]: 'CUTTER',
            [CUTTER_OLD]: 'CUTTER',
            [QUARRY]: 'QUARRY',
            [QUARRY_OLD]: 'QUARRY',
            [MINE]: 'MINE',
            [MINE_OLD]: 'MINE',
            [FARM]: 'FARM',
            [FARM_OLD]: 'FARM',
            [MANOR]: 'MANOR',
            [CASA]: 'CASA',
            [CELLARIUM]: 'CELLARIUM',
            [CACHE]: 'CACHE',
            [WHARF]: 'WHARF',
            [BAZAAR]: 'BAZAAR',
            [TOWN_HALL]: 'TOWN_HALL',
            [WALL]: 'WALL',
            [SAWMILL]: 'SAWMILL',
            [STONEMASON]: 'STONEMASON',
            [FOUNDRY]: 'FOUNDRY',
            [MILL]: 'MILL',
            [BARRACKS]: 'BARRACKS',
            [CITY_GUARD_HOUSE]: 'CITY_GUARD_HOUSE',
            [TRAINING_GROUND]: 'TRAINING_GROUND',
            [STABLES]: 'STABLES',
            [MOONGLOW]: 'MOONGLOW',
            [SHIPYARD]: 'SHIPYARD',
            [WORKSHOP]: 'WORKSHOP',
            [CASTLE]: 'CASTLE'
        };
        return keyMap[tile] || null;
    }
    
    /**
     * Render total costs section
     */
    renderTotalCosts() {
        const costs = this.calculateTotalCosts();
        const buildingLevel = this.gameEngine.buildingLevel || 10;
        const basilicaLevel = this.gameEngine.basilicaLevel || 10;
        const nextBuildingLevel = buildingLevel < 10 ? buildingLevel + 1 : null;
        const nextBasilicaLevel = basilicaLevel < 10 ? basilicaLevel + 1 : null;
        const buildRate = this.gameEngine?.building_rate || 1.0;
        
        // Determine number of columns based on available items
        const itemCount = 2 + (nextBuildingLevel ? 1 : 0) + (buildingLevel < 10 ? 1 : 0);
        const columns = itemCount >= 4 ? 2 : itemCount === 3 ? 3 : itemCount;
        
        // Show both levels if they differ
        const levelText = buildingLevel === basilicaLevel 
            ? `Level ${buildingLevel}` 
            : `Buildings: Level ${buildingLevel}, Basilica: Level ${basilicaLevel}`;
        
        return `
            <div class="optimization-card">
                <div class="optimization-card-header">
                    <h4>All Buildings, Walls & Basilica (${levelText})</h4>
                </div>
                <div class="optimization-card-body">
                    <div class="costs-grid" style="display: grid; grid-template-columns: repeat(${columns}, 1fr); gap: 16px;">
                        <div class="cost-item" style="font-size: 13px;">
                            <div class="cost-label" style="font-size: 12px; font-weight: 600; margin-bottom: 6px;">Current (${buildingLevel}):</div>
                            <div class="cost-value" style="font-size: 14px; margin-bottom: 4px;">${this.formatCost(costs.currentLevel)}</div>
                            ${costs.currentLevel.buildTime ? `
                                <div class="cost-time" style="font-size: 11px;">${this.formatBuildTimeWithIcon(costs.currentLevel.buildTime, buildRate)}</div>
                            ` : ''}
                        </div>
                        ${nextBuildingLevel ? `
                            <div class="cost-item" style="font-size: 13px;">
                                <div class="cost-label" style="font-size: 12px; font-weight: 600; margin-bottom: 6px;">Next (${nextBuildingLevel}):</div>
                                <div class="cost-value" style="font-size: 14px; margin-bottom: 4px;">${this.formatCost(costs.currentToNext)}</div>
                                ${costs.currentToNext.buildTime ? `
                                    <div class="cost-time" style="font-size: 11px;">${this.formatBuildTimeWithIcon(costs.currentToNext.buildTime, buildRate)}</div>
                                ` : ''}
                            </div>
                        ` : ''}
                        <div class="cost-item" style="font-size: 13px;">
                            <div class="cost-label" style="font-size: 12px; font-weight: 600; margin-bottom: 6px;">Total to Current:</div>
                            <div class="cost-value" style="font-size: 14px; margin-bottom: 4px;">${this.formatCost(costs.totalToCurrent)}</div>
                            ${costs.totalToCurrent.buildTime ? `
                                <div class="cost-time" style="font-size: 11px;">${this.formatBuildTimeWithIcon(costs.totalToCurrent.buildTime, buildRate)}</div>
                            ` : ''}
                        </div>
                        ${buildingLevel < 10 ? `
                            <div class="cost-item" style="font-size: 13px;">
                                <div class="cost-label" style="font-size: 12px; font-weight: 600; margin-bottom: 6px;">Total to Level 10:</div>
                                <div class="cost-value" style="font-size: 14px; margin-bottom: 4px;">${this.formatCost(costs.totalToLevel10)}</div>
                                ${costs.totalToLevel10.buildTime ? `
                                    <div class="cost-time" style="font-size: 11px;">${this.formatBuildTimeWithIcon(costs.totalToLevel10.buildTime, buildRate)}</div>
                                ` : ''}
                            </div>
                        ` : ''}
                    </div>
                </div>
            </div>
        `;
    }
    
    /**
     * Format cost for display with resource icons
     */
    formatCost(cost) {
        const parts = [];
        const iconStyle = 'width: 14px; height: 14px; vertical-align: middle; margin-right: 2px;';
        
        if (cost.wood > 0) {
            parts.push(`<img src="./assets/images/ui/icon_resource_wood.png" alt="Wood" style="${iconStyle}" onerror="this.style.display='none'">${this.formatNumber(cost.wood)}`);
        }
        if (cost.stone > 0) {
            parts.push(`<img src="./assets/images/ui/icon_resource_stone.png" alt="Stone" style="${iconStyle}" onerror="this.style.display='none'">${this.formatNumber(cost.stone)}`);
        }
        
        return parts.length > 0 ? parts.join(' ') : 'Free';
    }
    
    /**
     * Format build time with clock icon, applying build speed multiplier
     */
    formatBuildTimeWithIcon(buildTime, buildRate = 1.0) {
        if (!buildTime || buildTime === 0) return '';
        
        // Apply build speed multiplier from cabins
        const actualBuildTime = Math.max(1, Math.round(buildTime / buildRate));
        
        const iconStyle = 'width: 14px; height: 14px; vertical-align: middle; margin-right: 2px;';
        return `<img src="./assets/images/ui/icon_clock_orange.png" alt="Build Time" style="${iconStyle}" onerror="this.style.display='none'">${formatBuildTime(actualBuildTime)}`;
    }
    
    /**
     * Setup event listeners for weight sliders and buttons
     */
    setupEventListeners() {
        // Weight sliders and inputs
        ['wood', 'stone', 'iron', 'food', 'gold'].forEach(resource => {
            const slider = document.getElementById(`weight-${resource}`);
            const input = document.getElementById(`weight-input-${resource}`);
            
            // Helper function to update weight
            const updateWeight = (value) => {
                // Clamp value between min and max
                const clampedValue = Math.max(0, Math.min(3, parseFloat(value) || 0));
                const weightProp = `${resource}_weight`;
                this.gameEngine[weightProp] = clampedValue;
                
                // Save to settings
                settings.set(`${resource}_weight`, clampedValue);
                
                // Update all related elements
                const weightSection = document.getElementById(`weight-${resource}`)?.closest('.weight-section');
                if (weightSection) {
                    const valueSpan = weightSection.querySelector('.weight-value');
                    if (valueSpan) {
                        valueSpan.textContent = clampedValue.toFixed(2);
                    }
                    if (slider) {
                        slider.value = clampedValue;
                    }
                    if (input) {
                        input.value = clampedValue.toFixed(2);
                    }
                }
                
                if (this.onWeightChange) {
                    this.onWeightChange(resource, clampedValue);
                }
            };
            
            // Slider event
            if (slider) {
                slider.addEventListener('input', (e) => {
                    updateWeight(e.target.value);
                });
            }
            
            // Input box events
            if (input) {
                input.addEventListener('input', (e) => {
                    updateWeight(e.target.value);
                });
                
                input.addEventListener('blur', (e) => {
                    // Ensure value is properly formatted on blur
                    const value = parseFloat(e.target.value) || 0;
                    const clampedValue = Math.max(0, Math.min(3, value));
                    e.target.value = clampedValue.toFixed(2);
                    updateWeight(clampedValue);
                });
            }
        });
        
        // Add/Remove buttons
        this.container.querySelectorAll('[data-action]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const action = e.target.dataset.action;
                const resource = e.target.dataset.resource;
                
                if (action === 'add' && this.onAddBuilding) {
                    this.onAddBuilding(resource);
                } else if (action === 'remove' && this.onRemoveBuilding) {
                    this.onRemoveBuilding(resource);
                }
            });
        });
        
        // Add N buildings button
        const btnAddBuildings = document.getElementById('btn-add-buildings');
        if (btnAddBuildings) {
            btnAddBuildings.addEventListener('click', () => {
                const input = document.getElementById('n-buildings-add');
                const count = parseInt(input?.value || '10');
                if (count > 0 && this.onAddBuildings) {
                    this.onAddBuildings(count);
                }
            });
        }
        
        // Remove N buildings button
        const btnRemoveBuildings = document.getElementById('btn-remove-buildings');
        if (btnRemoveBuildings) {
            btnRemoveBuildings.addEventListener('click', () => {
                const input = document.getElementById('n-buildings-remove');
                const count = parseInt(input?.value || '5');
                if (count > 0 && this.onRemoveBuildings) {
                    this.onRemoveBuildings(count);
                }
            });
        }
        
        // Add N cabins button
        const btnAddCabins = document.getElementById('btn-add-cabins');
        if (btnAddCabins) {
            btnAddCabins.addEventListener('click', () => {
                const input = document.getElementById('n-cabins-add');
                const count = parseInt(input?.value || '10');
                const optimizeCheckbox = document.getElementById('cabin-optimize-mode');
                const useOptimize = optimizeCheckbox?.checked || false;
                if (count > 0 && this.onAddCabins) {
                    this.onAddCabins(count, useOptimize);
                }
            });
        }
        
        // Remove N cabins button
        const btnRemoveCabins = document.getElementById('btn-remove-cabins');
        if (btnRemoveCabins) {
            btnRemoveCabins.addEventListener('click', () => {
                const input = document.getElementById('n-cabins-remove');
                const count = parseInt(input?.value || '5');
                if (count > 0 && this.onRemoveCabins) {
                    this.onRemoveCabins(count);
                }
            });
        }
        
        // Cabin optimize mode checkbox
        const cabinOptimizeMode = document.getElementById('cabin-optimize-mode');
        const cabinNote = document.getElementById('cabin-note');
        if (cabinOptimizeMode && cabinNote) {
            cabinOptimizeMode.addEventListener('change', (e) => {
                if (e.target.checked) {
                    cabinNote.textContent = 'Optimizes cabin placement, may replace other buildings';
                } else {
                    cabinNote.textContent = 'cabins place on OPEN spots, won\'t replace others';
                }
            });
        }
        
        // Lock All Buildings button
        const btnLockBuildings = document.getElementById('btn-lock-buildings');
        if (btnLockBuildings) {
            btnLockBuildings.addEventListener('click', () => {
                if (this.onLockAllBuildings) {
                    this.onLockAllBuildings();
                }
            });
        }
        
        // Lock All Resources button
        const btnLockResources = document.getElementById('btn-lock-resources');
        if (btnLockResources) {
            btnLockResources.addEventListener('click', () => {
                if (this.onLockAllResources) {
                    this.onLockAllResources();
                }
            });
        }
        
        // Unlock All button
        const btnUnlockAll = document.getElementById('btn-unlock-all');
        if (btnUnlockAll) {
            btnUnlockAll.addEventListener('click', () => {
                if (this.onUnlockAll) {
                    this.onUnlockAll();
                }
            });
        }
        
        // Undo button
        const btnUndo = document.getElementById('btn-undo');
        if (btnUndo) {
            btnUndo.addEventListener('click', () => {
                if (this.onUndo) {
                    this.onUndo();
                }
            });
        }
        
        // Redo button
        const btnRedo = document.getElementById('btn-redo');
        if (btnRedo) {
            btnRedo.addEventListener('click', () => {
                if (this.onRedo) {
                    this.onRedo();
                }
            });
        }
        
        // Reset Map button
        const btnResetMap = document.getElementById('btn-reset-map');
        if (btnResetMap) {
            btnResetMap.addEventListener('click', () => {
                if (this.onResetMap) {
                    this.onResetMap();
                }
            });
        }
    }
    
    /**
     * Format number with commas
     */
    formatNumber(num) {
        return Math.round(num).toLocaleString();
    }
    
    /**
     * Set layout resources from array
     * Array format: [0, 0, 0, 0, 1, requestWood, requestStone, requestIron, requestFood, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, excessWood, excessStone, excessIron, excessFood]
     * - Indices 0-4: [0, 0, 0, 0, 1] - Skip
     * - Indices 5-8: Request Resources (Wood, Stone, Iron, Food)
     * - Indices 9-18: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0] - Skip
     * - Indices 19-22: Excess Resources (Wood, Stone, Iron, Food)
     * @param {number[]} resourceArray - Array of resource values
     */
    setLayoutResources(resourceArray) {
        // Always show the section now that it's editable
        const section = this.container.querySelector('#layout-resources-section');
        if (section) {
            section.style.display = 'block';
        }
        
        if (!Array.isArray(resourceArray) || resourceArray.length < 23) {
            // If invalid data, just set to zeros
            this.layoutResources = null;
            this.updateLayoutResourcesDisplay(
                { wood: 0, stone: 0, iron: 0, food: 0 },
                { wood: 0, stone: 0, iron: 0, food: 0 }
            );
            return;
        }
        
        this.layoutResources = resourceArray;
        
        // Extract Request Resources (indices 5-8)
        const requestResources = {
            wood: resourceArray[5] || 0,
            stone: resourceArray[6] || 0,
            iron: resourceArray[7] || 0,
            food: resourceArray[8] || 0
        };
        
        // Extract Excess Resources (indices 19-22)
        const excessResources = {
            wood: resourceArray[19] || 0,
            stone: resourceArray[20] || 0,
            iron: resourceArray[21] || 0,
            food: resourceArray[22] || 0
        };
        
        // Update display
        this.updateLayoutResourcesDisplay(requestResources, excessResources);
    }
    
    /**
     * Update the layout resources display
     */
    updateLayoutResourcesDisplay(requestResources, excessResources) {
        const section = this.container.querySelector('#layout-resources-section');
        if (!section) return;
        
        // Always show the section now that it's editable
        section.style.display = 'block';
        
        // Update Request Resources inputs
        const requestWoodEl = this.container.querySelector('#request-wood');
        const requestStoneEl = this.container.querySelector('#request-stone');
        const requestIronEl = this.container.querySelector('#request-iron');
        const requestFoodEl = this.container.querySelector('#request-food');
        
        if (requestWoodEl) requestWoodEl.value = requestResources.wood || 0;
        if (requestStoneEl) requestStoneEl.value = requestResources.stone || 0;
        if (requestIronEl) requestIronEl.value = requestResources.iron || 0;
        if (requestFoodEl) requestFoodEl.value = requestResources.food || 0;
        
        // Update Excess Resources inputs
        const excessWoodEl = this.container.querySelector('#excess-wood');
        const excessStoneEl = this.container.querySelector('#excess-stone');
        const excessIronEl = this.container.querySelector('#excess-iron');
        const excessFoodEl = this.container.querySelector('#excess-food');
        
        if (excessWoodEl) excessWoodEl.value = excessResources.wood || 0;
        if (excessStoneEl) excessStoneEl.value = excessResources.stone || 0;
        if (excessIronEl) excessIronEl.value = excessResources.iron || 0;
        if (excessFoodEl) excessFoodEl.value = excessResources.food || 0;
    }
    
    /**
     * Get layout resources array from input fields
     * @returns {number[]|null} Array of resource values or null
     */
    getLayoutResourcesArray() {
        // Get values from input fields
        const requestWood = parseInt(this.container.querySelector('#request-wood')?.value || '0', 10);
        const requestStone = parseInt(this.container.querySelector('#request-stone')?.value || '0', 10);
        const requestIron = parseInt(this.container.querySelector('#request-iron')?.value || '0', 10);
        const requestFood = parseInt(this.container.querySelector('#request-food')?.value || '0', 10);
        const excessWood = parseInt(this.container.querySelector('#excess-wood')?.value || '0', 10);
        const excessStone = parseInt(this.container.querySelector('#excess-stone')?.value || '0', 10);
        const excessIron = parseInt(this.container.querySelector('#excess-iron')?.value || '0', 10);
        const excessFood = parseInt(this.container.querySelector('#excess-food')?.value || '0', 10);
        
        // Build the full 23-element array format
        // Format: [0, 0, 0, 0, 1, requestWood, requestStone, requestIron, requestFood, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, excessWood, excessStone, excessIron, excessFood]
        return [
            0, 0, 0, 0, 1, // Indices 0-4
            requestWood,
            requestStone,
            requestIron,
            requestFood, // Indices 5-8
            0, 0, 0, 0, 1, 0, 0, 0, 0, 0, // Indices 9-18
            excessWood,
            excessStone,
            excessIron,
            excessFood // Indices 19-22
        ];
    }
    
    /**
     * Update the panel when game engine state changes
     */
    update() {
        // Preserve layout resources before re-rendering
        const preservedResources = this.layoutResources;
        this.render();
        // Restore layout resources display after render
        if (preservedResources) {
            this.setLayoutResources(preservedResources);
        }
    }
}

